import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { Component, ElementRef, forwardRef, Input, ViewChild } from "@angular/core";

import { FieldCoreComponent } from "../FieldCoreComponent";
import { STRING } from "../../../constants/string";
import { Base64 } from "../../../services/Base64";

const getFileName = (fileName: string): string => {
  const splitted = fileName.split(STRING.DOT);
  return splitted.length > 1 ? splitted[splitted.length - 2] : fileName;
};

const getFileExtension = (fileName: string, extension: string): string => {
  if (extension) {
    return extension;
  }

  const splitted = fileName.split(STRING.DOT);
  return splitted.length > 1 ? splitted[splitted.length - 1] : STRING.EMPTY;
};

enum UPLOAD_STATUSES {
  NONE = "",
  PROGRESS = "progress",
  DONE = "done",
}

@Component({
  selector: "med-upload-file",
  templateUrl: "./med-upload-file.component.html",
  styleUrls: ["./med-upload-file.component.scss"],
  providers: [{
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MedUploadFileComponent),
  }],
})
export class MedUploadFileComponent extends FieldCoreComponent<string | File> {
  private _rawFileName: string = STRING.EMPTY;
  private _fileName: string = STRING.EMPTY;
  private _extension: string = STRING.EMPTY;
  readonly UPLOAD_STATUSES = UPLOAD_STATUSES;
  status: UPLOAD_STATUSES = UPLOAD_STATUSES.NONE;
  type: string = STRING.EMPTY;

  @Input() accept: string = STRING.EMPTY; // https://html.spec.whatwg.org/multipage/input.html#attr-input-accept
  @Input() binary = false;
  @ViewChild("inputRef") inputRef!: ElementRef<HTMLInputElement>;

  override get classList(): string {
    return [super.classList, this.status].join(STRING.SPACE);
  }

  get extension() {
    return this._extension;
  }

  get fileName() {
    return this._fileName;
  }

  set fileName(value: string) {
    this._rawFileName = value;
    this._fileName = getFileName(value);
  }

  @Input() set defaultValue(val: string | undefined) {
    this.fileName = val || STRING.EMPTY;
    this.type = getFileExtension(val || STRING.EMPTY, this.extension);
    this.status = val ? UPLOAD_STATUSES.DONE : UPLOAD_STATUSES.NONE;
  }

  @Input() set extension(value) {
    this._extension = value;
    this.type = getFileExtension(this._rawFileName, value);
  }

  async uploadFile({ target: { files } }: any) {
    this.status = UPLOAD_STATUSES.PROGRESS;

    const [firstFile]: Array<File | null> = [...files];
    if (!firstFile) {
      return;
    }

    this.fileName = firstFile.name;
    this.type = getFileExtension(firstFile.name, this.extension);

    this.value = this.binary ? firstFile : await Base64.encode(firstFile);

    this.onInput.emit(this.value);

    this.status = UPLOAD_STATUSES.DONE;
  }

  clear(event: Event) {
    event.preventDefault();

    this.value = STRING.EMPTY;
    this.inputRef.nativeElement.value = STRING.EMPTY;
    this.fileName = this.placeholder;
    this.status = UPLOAD_STATUSES.NONE;
    this.onInput.emit(this.value);
  }
}
